import React, { useEffect, useState } from "react";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import useSettings from "../../hooks/useSettings";
import api from "../../services/api";
import { toast } from 'react-toastify';
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from '../../translate/i18n.js';
import PlansManagerImproved from "../PlansManager/PlansManagerImproved";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  sectionCard: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.mode === 'dark' 
        ? '0 4px 16px rgba(0,0,0,0.5)' 
        : '0 4px 16px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
  },
  sectionHeader: {
    background: theme.mode === 'dark' 
      ? 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)' 
      : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: 'white',
    padding: theme.spacing(2),
    borderRadius: '12px 12px 0 0',
    marginBottom: 0,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  settingItem: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
    transition: 'all 0.2s ease',
    height: '100%',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e3f2fd',
      borderColor: theme.mode === 'dark' ? theme.palette.primary.light : '#1976d2',
    },
  },
  settingLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  settingDescription: {
    fontSize: '11px',
    color: theme.palette.text.secondary || theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    fontStyle: 'italic',
    lineHeight: 1.4,
  },
  textField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      color: theme.palette.text.primary,
      '& fieldset': {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiFormHelperText-root': {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
  },
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      '& fieldset': {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
  },
}));

export default function SubscriptionsConfig() {
  const classes = useStyles();
  const { update } = useSettings();

  const [paypalClientId, setPaypalClientId] = useState("");
  const [paypalClientSecret, setPaypalClientSecret] = useState("");
  const [paypalMode, setPaypalMode] = useState("sandbox");
  const [stripeSecretKey, setStripeSecretKey] = useState("");
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [loadingPaypalClientId, setLoadingPaypalClientId] = useState(false);
  const [loadingPaypalClientSecret, setLoadingPaypalClientSecret] = useState(false);
  const [loadingPaypalMode, setLoadingPaypalMode] = useState(false);
  const [loadingStripeSecretKey, setLoadingStripeSecretKey] = useState(false);
  const [loadingStripeWebhookSecret, setLoadingStripeWebhookSecret] = useState(false);
  const [loadingCurrency, setLoadingCurrency] = useState(false);

  useEffect(() => {
    const loadGlobalSettings = async () => {
      try {
        const globalSettingsKeys = [
          'paypalClientId',
          'paypalClientSecret',
          'paypalMode',
          'stripeSecretKey',
          'stripeWebhookSecret',
          'currency'
        ];

        const settingsPromises = globalSettingsKeys.map(key =>
          api.get(`/settings/${key}`).catch(err => {
            // ✅ Si el setting no existe (404), retornar null en lugar de fallar
            if (err?.response?.status === 404) {
              return { data: null, status: 404 };
            }
            throw err;
          })
        );

        const settingsResults = await Promise.all(settingsPromises);

        // Payment gateways
        if (settingsResults[0]?.data?.value) {
          setPaypalClientId(settingsResults[0].data.value);
        }
        if (settingsResults[1]?.data?.value) {
          setPaypalClientSecret(settingsResults[1].data.value);
        }
        if (settingsResults[2]?.data?.value) {
          setPaypalMode(settingsResults[2].data.value);
        } else {
          setPaypalMode("sandbox"); // Valor por defecto
        }
        if (settingsResults[3]?.data?.value) {
          setStripeSecretKey(settingsResults[3].data.value);
        }
        if (settingsResults[4]?.data?.value) {
          setStripeWebhookSecret(settingsResults[4].data.value);
        }
        // ✅ Currency: Verificar si existe y tiene valor, si no usar por defecto
        if (settingsResults[5]?.data?.value) {
          setCurrency(settingsResults[5].data.value);
        } else if (settingsResults[5]?.status === 404 || !settingsResults[5]?.data) {
          // Si el setting no existe, usar valor por defecto
          setCurrency("USD");
        } else {
          setCurrency("USD"); // Valor por defecto
        }
      } catch (error) {
        console.warn('Error cargando settings globales, usando valores por defecto:', error);
      }
    };

    loadGlobalSettings();
  }, []);

  async function handleSavePaypalClientId() {
    const value = paypalClientId;
    setLoadingPaypalClientId(true);
    try {
      await update({
        key: "paypalClientId",
        value,
      });
      toast.success("PayPal Client ID guardado correctamente");
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando paypalClientId:', error);
    } finally {
      setLoadingPaypalClientId(false);
    }
  }

  async function handleSavePaypalClientSecret() {
    const value = paypalClientSecret;
    setLoadingPaypalClientSecret(true);
    try {
      await update({
        key: "paypalClientSecret",
        value,
      });
      toast.success("PayPal Client Secret guardado correctamente");
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando paypalClientSecret:', error);
    } finally {
      setLoadingPaypalClientSecret(false);
    }
  }

  async function handleSavePaypalMode(value) {
    setLoadingPaypalMode(true);
    try {
      await update({
        key: "paypalMode",
        value,
      });
      toast.success("Modo PayPal actualizado correctamente");
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando paypalMode:', error);
    } finally {
      setLoadingPaypalMode(false);
    }
  }

  async function handleSaveStripeSecretKey() {
    const value = stripeSecretKey;
    setLoadingStripeSecretKey(true);
    try {
      await update({
        key: "stripeSecretKey",
        value,
      });
      toast.success("Stripe Secret Key guardado correctamente");
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando stripeSecretKey:', error);
    } finally {
      setLoadingStripeSecretKey(false);
    }
  }

  async function handleSaveStripeWebhookSecret() {
    const value = stripeWebhookSecret;
    setLoadingStripeWebhookSecret(true);
    try {
      await update({
        key: "stripeWebhookSecret",
        value,
      });
      toast.success("Stripe Webhook Secret guardado correctamente");
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando stripeWebhookSecret:', error);
    } finally {
      setLoadingStripeWebhookSecret(false);
    }
  }

  async function handleSaveCurrency(value) {
    setLoadingCurrency(true);
    try {
      const result = await update({
        key: "currency",
        value,
      });
      // ✅ Actualizar el estado local con el valor guardado confirmado desde el servidor
      // El hook useSettings retorna responseData que es el objeto setting completo
      if (result?.value) {
        setCurrency(result.value);
      } else {
        // Si no viene el valor en result, usar el valor que se intentó guardar
        setCurrency(value);
      }
      toast.success("Moneda actualizada correctamente");
    } catch (error) {
      toast.error(i18n.t("settings.error"));
      console.error('Error guardando currency:', error);
      // ✅ Revertir el estado si hay error - recargar el valor actual desde el servidor
      try {
        const response = await api.get(`/settings/currency`);
        if (response?.data?.value) {
          setCurrency(response.data.value);
        } else {
          // Si no existe, mantener el valor actual del estado
          console.warn('Setting currency no encontrado, manteniendo valor actual del estado');
        }
      } catch (reloadError) {
        console.error('Error recargando currency:', reloadError);
        // Mantener el valor actual del estado si no se puede recargar
      }
    } finally {
      setLoadingCurrency(false);
    }
  }

  return (
    <Box className={classes.container}>
      <Card className={classes.sectionCard}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span role="img" aria-label="Pagos">💳</span> Métodos de Pago
          </Typography>
        </Box>
        <CardContent className={classes.sectionContent}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Configura las credenciales de PayPal y Stripe para habilitar los pagos recurrentes. 
            <strong> Nota:</strong> Después de guardar, necesitarás reiniciar el servidor backend para que los cambios surtan efecto.
          </Typography>
          
          <Grid container spacing={2}>
            {/* PayPal Configuration */}
            <Grid item xs={12}>
              <Typography variant="h6" style={{ marginBottom: 16, marginTop: 8 }}>
                PayPal
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  Client ID de PayPal
                </Typography>
                <Typography className={classes.settingDescription}>
                  ID de cliente de tu aplicación PayPal
                </Typography>
                <TextField
                  id="paypalClientId"
                  name="paypalClientId"
                  margin="dense"
                  label="PayPal Client ID"
                  variant="outlined"
                  type="password"
                  value={paypalClientId}
                  onChange={(e) => setPaypalClientId(e.target.value)}
                  onBlur={handleSavePaypalClientId}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingPaypalClientId ? "Guardando..." : "Ejemplo: AQkquBDf1zctJOWLbrloE4zQnlX4rQvBfhXUZHX96zShVqSB2ikbkGg"}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  Client Secret de PayPal
                </Typography>
                <Typography className={classes.settingDescription}>
                  Secreto de cliente de tu aplicación PayPal
                </Typography>
                <TextField
                  id="paypalClientSecret"
                  name="paypalClientSecret"
                  margin="dense"
                  label="PayPal Client Secret"
                  variant="outlined"
                  type="password"
                  value={paypalClientSecret}
                  onChange={(e) => setPaypalClientSecret(e.target.value)}
                  onBlur={handleSavePaypalClientSecret}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingPaypalClientSecret ? "Guardando..." : "Secreto de tu aplicación PayPal"}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  Modo de PayPal
                </Typography>
                <Typography className={classes.settingDescription}>
                  Selecciona entre modo sandbox (pruebas) o live (producción)
                </Typography>
                <FormControl fullWidth margin="dense" variant="outlined">
                  <InputLabel>Modo</InputLabel>
                  <Select
                    value={paypalMode}
                    onChange={(e) => {
                      setPaypalMode(e.target.value);
                      handleSavePaypalMode(e.target.value);
                    }}
                    label="Modo"
                    disabled={loadingPaypalMode}
                  >
                    <MenuItem value="sandbox">Sandbox (Pruebas)</MenuItem>
                    <MenuItem value="live">Live (Producción)</MenuItem>
                  </Select>
                  {loadingPaypalMode && (
                    <FormHelperText>Guardando...</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>

            {/* Stripe Configuration */}
            <Grid item xs={12} style={{ marginTop: 16 }}>
              <Typography variant="h6" style={{ marginBottom: 16 }}>
                Stripe
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  Secret Key de Stripe
                </Typography>
                <Typography className={classes.settingDescription}>
                  Clave secreta de tu cuenta de Stripe (sk_test_... o sk_live_...)
                </Typography>
                <TextField
                  id="stripeSecretKey"
                  name="stripeSecretKey"
                  margin="dense"
                  label="Stripe Secret Key"
                  variant="outlined"
                  type="password"
                  value={stripeSecretKey}
                  onChange={(e) => setStripeSecretKey(e.target.value)}
                  onBlur={handleSaveStripeSecretKey}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingStripeSecretKey ? "Guardando..." : "Ejemplo: sk_test_51AbCdEf..."}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  Webhook Secret de Stripe
                </Typography>
                <Typography className={classes.settingDescription}>
                  Secreto del webhook de Stripe para verificar eventos (whsec_...)
                </Typography>
                <TextField
                  id="stripeWebhookSecret"
                  name="stripeWebhookSecret"
                  margin="dense"
                  label="Stripe Webhook Secret"
                  variant="outlined"
                  type="password"
                  value={stripeWebhookSecret}
                  onChange={(e) => setStripeWebhookSecret(e.target.value)}
                  onBlur={handleSaveStripeWebhookSecret}
                  fullWidth
                  className={classes.textField}
                  helperText={loadingStripeWebhookSecret ? "Guardando..." : "Ejemplo: whsec_1234567890abcdef"}
                />
              </Box>
            </Grid>

            {/* Currency Configuration */}
            <Grid item xs={12} style={{ marginTop: 16 }}>
              <Typography variant="h6" style={{ marginBottom: 16 }}>
                Moneda
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  Moneda Preferida
                </Typography>
                <Typography className={classes.settingDescription}>
                  Selecciona la moneda que se mostrará en los planes y facturas
                </Typography>
                <FormControl fullWidth margin="dense" variant="outlined">
                  <InputLabel>Moneda</InputLabel>
                  <Select
                    value={currency}
                    onChange={(e) => {
                      // ✅ No actualizar el estado local inmediatamente, esperar a que se guarde
                      handleSaveCurrency(e.target.value);
                    }}
                    label="Moneda"
                    disabled={loadingCurrency}
                  >
                    <MenuItem value="USD">USD - Dólar Estadounidense ($)</MenuItem>
                    <MenuItem value="EUR">EUR - Euro (€)</MenuItem>
                    <MenuItem value="GBP">GBP - Libra Esterlina (£)</MenuItem>
                    <MenuItem value="BRL">BRL - Real Brasileño (R$)</MenuItem>
                    <MenuItem value="MXN">MXN - Peso Mexicano ($)</MenuItem>
                    <MenuItem value="ARS">ARS - Peso Argentino ($)</MenuItem>
                    <MenuItem value="CLP">CLP - Peso Chileno ($)</MenuItem>
                    <MenuItem value="COP">COP - Peso Colombiano ($)</MenuItem>
                    <MenuItem value="PEN">PEN - Sol Peruano (S/)</MenuItem>
                  </Select>
                  {loadingCurrency && (
                    <FormHelperText>Guardando...</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider style={{ margin: "24px 0" }} />

      <Card className={classes.sectionCard}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span role="img" aria-label="Planes">📋</span> Planes
          </Typography>
        </Box>
        <CardContent className={classes.sectionContent}>
          <PlansManagerImproved />
        </CardContent>
      </Card>
    </Box>
  );
}

